import { useEffect } from "react";
import { signal } from "@preact/signals-react";
import { FormattedMessage as FM } from "react-intl";

import DatePicker from "componentsv2/form/DatePicker";
import Dropdown from "componentsv2/form/Dropdown";
import Selection from "componentsv2/form/Selection";
import ModalWizard from "componentsv2/modal/ModalWizard";
import MenuReports from "componentsv2/menu/MenuReports";
import WizardCreateShifts from "./WizardCreateShifts";
import WizardUpdate from "./WizardUpdate";
import EmptyTable from "./EmptyTable";
import ShiftList from "./ShiftList";
import { layout as layoutReport } from "./reportShiftScheduling";
import Sheet from "./Sheet";
import SearchBar from "componentsv2/widgets/SearchBar";
import { useSearch } from "./useSearch";
import proxy from "api/proxy";
import { UserIcon, AngleRightIcon, XMarkIcon } from "components/Icons";
import { cn } from "tools/ui";
import { useTable } from "storev2/tableStore";
import { useLayout } from "hooks/layout";
import { layoutShift } from "./layoutShift";
import {
  getSections,
  fetchStaffConfiguration,
  fetchMealConsumptionMap,
  fetchHolidays,
} from "./services";
import Notifications from "./Notifications";
import Issues from "./Issues";

const selectedDate = signal();
const selectedSections = signal([]);
const pickedSection = signal(null);
const selectedDays = signal(null);
const dataSheet = signal(null);
const weekHoursWork = signal(42);
const mealMap = signal(new Map());
const holidays = signal(new Set());
const model = "staff.shift";
const wordSearch = signal(null);
const sidePanelOpen = signal(true);

const initialState = {
  selectedStart: signal(null),
  selectedEnd: signal(null),
  selectedSection: signal(null),
  selectedKind: signal(null),
  selectedPosition: signal(null),
};

const initialUpdate = {
  selectedDate: signal(null),
  section: signal(null),
  activity: signal(null),
  billable: signal(null),
  notes: signal(null),
  position: signal(null),
  enterTimestamp: signal(null),
  exitTimestamp: signal(null),
};


function getEmployeeIdsFromData(data) {
  const ids = new Set();
  for (const rec of Object.values(data?.records || {})) {
    for (const emp of rec.employees || []) {
      const id = Number(emp?.employee?.[0]);
      if (id) ids.add(id);
    }
  }
  return [...ids];
}

function getRecordId(value) {
  if (value && typeof value === "object") {
    return value.id ?? value[0] ?? null;
  }
  return value ?? null;
}

async function getDataSheet(wordSearch) {
  const employee = wordSearch;
  if (!selectedDate.value || !selectedDays.value) return null;
  const viewportWidth = document.documentElement.clientWidth;
  const start_date = selectedDate.value;
  let days = Number(selectedDays.value.id);
  if (viewportWidth < 450) {
    days = 3;
  } else if (viewportWidth < 768) {
    days = 7;
  }
  const sections = selectedSections.value.map((s) => ({
    id: s.id,
    name: s.name || s.rec_name,
  }));
  const ctxArgs = {
    model,
    method: "get_shift_schedule",
    args: [
      {
        sections,
        start_date,
        employee,
        days: days,
      },
    ],
  };
  const { data } = await proxy.methodCall(ctxArgs);
  if (!data) return null;

  const { headDays } = data;
  let extraShifts = [];
  if (headDays?.length) {
    const domain = [
      ["shift_date", "in", headDays],
      ["state", "!=", "cancelled"],
    ];
    if (sections.length) {
      domain.push(["section", "in", sections.map((s) => s.id)]);
    } else {
      domain.push(["employee", "in", getEmployeeIdsFromData(data)]);
    }
    const { data: shifts } = await proxy.search(
      "staff.shift",
      domain,
      [
        "id",
        "shift_date",
        "employee",
        "shift_kind",
        "state",
        "position",
        "section",
      ],
    );
    extraShifts = (shifts || []).map((s) => ({
      ...s,
      section: getRecordId(s.section),
    }));
  }
  return { ...data, extraShifts };
}

const RANGE_DAYS = [
  { id: 15, name: "15" },
  { id: 30, name: "30" },
];

function ShiftSchedule() {
  const layout = useLayout(layoutShift);
  const accessBill = layout?.extraModules.staff_access_bill === "activated";
  const crmStaff = layout?.extraModules.crm_staff === "activated";
  const extraModules = layout?.extraModules;

  useEffect(() => {
    selectedDate.value = null;
    selectedSections.value = [];
    pickedSection.value = null;
    selectedDays.value = null;
    wordSearch.value = null;
    dataSheet.value = null;

    fetchStaffConfiguration().then((config) => {
      if (config?.week_hours_work) weekHoursWork.value = config.week_hours_work;
    });
    fetchMealConsumptionMap().then((map) => {
      if (map) mealMap.value = map;
    });
    fetchHolidays().then((set) => {
      if (set) holidays.value = set;
    });
  }, []);

  async function refetch() {
    await handleSearch();
  }

  async function onDelete(rec) {
    const args = {
      model,
      ids: [rec.id],
    };
    await proxy.remove(args);
  }

  async function onSave(rec) {
    if (rec.id) {
      const {
        contracting_state,
        finished_date,
        has_contract,
        employee_position,
        ...shiftData
      } = rec;
      const args = {
        model,
        storeRec: shiftData,
        fieldsNames: ["id"],
      };
      const { data, error } = await proxy.saveQuery(args);
      if (error) {
        return {
          error:
            error?.error || error?.message || error?.detail || String(error),
        };
      }
      if (rec.id > 0) {
        return { id: rec.id };
      }
      if (data) {
        return { id: data[0].id };
      }
    }
    return { error: "No se pudo guardar el turno" };
  }

  function onReset() {
    const { resetTable } = useTable.getState();
    resetTable("staff.shift");
  }

  async function onChange(field, value) {
    if (field === "section") {
      pickedSection.value = null;
      if (!value?.id) return;
      if (selectedSections.value.some((s) => s.id === value.id)) return;
      selectedSections.value = [...selectedSections.value, value];
    }
    handleSearch();
  }

  function removeSection(sectionId) {
    selectedSections.value = selectedSections.value.filter(
      (s) => s.id !== sectionId,
    );
    handleSearch();
  }

  async function handleSearch(word) {
    if (word !== undefined) {
      wordSearch.value = word || null;
    }
    // if (selectedDate.value && selectedSection.value && selectedDays.value) {
    const hasFilter =
      selectedSections.value.length > 0 || wordSearch.value?.trim();
    if (!hasFilter) {
      dataSheet.value = null;
      return;
    }
    if (selectedDate.value && selectedDays.value) {
      dataSheet.value = null;
      const data = await getDataSheet(wordSearch.value);
      dataSheet.value = data;
    }
  }

  const shifts = useSearch({
    model: "staff.shift.kind",
    domain: [],
    fieldsNames: [
      "id",
      "name",
      "code",
      "start",
      "end",
      "start_rest",
      "end_rest",
      "mode",
      "total_time",
    ],
    type: "Map",
  });

  const activities = useSearch({
    model: "staff.event_category",
    domain: [["type_event", "=", "overtime"]],
    fieldsNames: ["id", "name"],
    type: "Map",
  });

  const attrsDate = {
    label: "staff.shift_scheduling.select_date",
    name: "shift_date",
    onChange: onChange,
    field: {
      required: true,
    },
  };

  const attrsDays = {
    label: "staff.shift_scheduling.days",
    name: "days",
    onChange: onChange,
    field: {
      required: true,
    },
    default: { id: 15, name: "15" },
    options: RANGE_DAYS,
  };

  const attrsSection = {
    label: "staff.shift_scheduling.section",
    name: "section",
    type: "many2one",
    model: "staff.section",
    onChange: onChange,
    searchable: true,
    preloaded: true,
    preOptions: getSections,
    field: {
      required: true,
    },
  };

  const wizAddAttrs = {
    label: "staff.shift_scheduling.add_employees",
    model: "staff.shift_scheduling.add_employees",
    icon: <UserIcon className="w-5 h-5 fill-current text-neutral-700" />,
    color: "amber",
    Component: WizardCreateShifts,
    style: "w-1/2 md:w-30 lg:w-48",
    args: { shifts, refetch, initialState },
    buttons: [],
  };

  const wizUpdateAttrs = {
    label: "staff.shift_scheduling.update",
    model: "staff.shift_scheduling.update",
    icon: <UserIcon className="w-5 h-5 fill-current text-neutral-100" />,
    color: "black",
    Component: WizardUpdate,
    style: "w-1/2 md:w-30 lg:w-48",
    args: { activities, refetch, extraModules, initialState: initialUpdate },
  };

  const reports = [
    {
      name: "staff_access.shift_scheduling.report",
      description: "staff_access.shift_scheduling_report.description",
      layout: layoutReport(),
    },
  ];

  return (
    <div
      id="staff-schedule-screen"
      className="flex w-full h-full px-2 md:pl-4 pr-2 overflow-x-hidden"
    >
      <div className="pt-3 w-full md:w-auto md:flex-1 md:min-w-0 md:pr-6 flex flex-col h-full">
        <div className="mb-4 flex flex-col gap-3 shrink-0">
          <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 w-full">
            <h2 className="text-2xl md:text-3xl font-bold text-neutral-600 min-w-[400px]">
              <FM id="staff.shift_scheduling" />
            </h2>
            <div className="hidden md:flex flex-wrap items-center gap-2">
              <ModalWizard {...wizAddAttrs} resetData={initialState} />
              {(accessBill || crmStaff) && (
                <>
                  <ModalWizard
                    {...wizUpdateAttrs}
                    resetData={initialUpdate}
                    onReset={onReset}
                  />
                </>
              )}
              <MenuReports reports={reports} />
              <ShiftList shifts={shifts} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:flex md:flex-wrap md:items-end md:gap-x-5 md:gap-y-4 w-full">
            <div className="col-span-2 md:col-span-1 md:min-w-[180px] md:flex-1 md:max-w-[280px]">
              <Dropdown {...attrsSection} data={pickedSection} />
            </div>
            <div className="w-full md:w-[160px]">
              <DatePicker {...attrsDate} data={selectedDate} />
            </div>
            <div className="w-full md:w-[160px]">
              <Selection {...attrsDays} data={selectedDays} />
            </div>
            <div className="col-span-2 md:col-span-1 md:min-w-[200px] md:flex-1 md:max-w-[400px] mb-1 xl:mb-1">
              <SearchBar handleSearch={handleSearch} />
            </div>
          </div>
          {selectedSections.value.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedSections.value.map((s) => (
                <span
                  key={s.id}
                  className="flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-amber-100 border border-amber-300 text-xs font-semibold text-amber-900"
                >
                  {s.name || s.rec_name}
                  <button
                    type="button"
                    onClick={() => removeSection(s.id)}
                    aria-label="Quitar sección"
                    className="rounded-full hover:bg-amber-200 active:scale-90 cursor-pointer"
                  >
                    <XMarkIcon className="size-4 fill-current" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 min-h-0 w-full">
          {dataSheet.value ? (
            <Sheet
              data={dataSheet.value}
              onSave={onSave}
              onDelete={onDelete}
              shifts={shifts}
              selectedDays={selectedDays.value?.id}
              layoutShift={layout}
              weekHoursWork={weekHoursWork.value}
              mealMap={mealMap.value}
              holidays={holidays.value}
              refetch={refetch}
            />
          ) : (
            <EmptyTable style="w-full" />
          )}
        </div>
      </div>
      <div
        className={cn(
          "hidden md:flex relative shrink-0 h-full border-l border-slate-200 bg-slate-50/50 transition-[width] duration-300",
          sidePanelOpen.value ? "w-1/5" : "w-6",
        )}
      >
        <button
          type="button"
          onClick={() => (sidePanelOpen.value = !sidePanelOpen.value)}
          aria-label={sidePanelOpen.value ? "Recoger panel" : "Expandir panel"}
          aria-expanded={sidePanelOpen.value}
          className="absolute -left-3 top-4 z-20 flex size-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-900 cursor-pointer"
        >
          <AngleRightIcon
            className={cn(
              "size-3 fill-current transition-transform duration-300",
              !sidePanelOpen.value && "rotate-180",
            )}
          />
        </button>
        <div
          className={cn(
            "flex flex-col w-full px-4 pt-3 overflow-y-auto overflow-x-hidden transition-opacity duration-300",
            sidePanelOpen.value ? "opacity-100" : "invisible opacity-0",
          )}
        >
          <Issues sections={selectedSections.value} />
          <Notifications />
        </div>
      </div>
    </div>
  );
}

export default ShiftSchedule;
